import React, { useEffect, useState } from 'react';

import useOrgSettingsStore from '../../stores/orgSettingsStore';

/**
 * ProcedurePaymentSettings — the per-org "auto-complete procedures on
 * payment" toggle (W4 / C3).
 *
 * Lives on the admin Settings page beside LabModuleSettings /
 * AdmissionModuleSettings (same card pattern), gated by the page's own
 * isAdmin check — a non-admin never reaches this card.
 *
 * Reads/writes the single source of truth in orgSettingsStore
 * (autoCompleteProcedures). GET is the existing procedure-payment settings
 * endpoint; PUT is the new C3 endpoint. If the GET fails (e.g. an old
 * backend that doesn't yet serve this field/route), the card degrades to an
 * "unavailable" state instead of crashing — the flag stays at its safe
 * default (off).
 *
 */
export default function ProcedurePaymentSettings() {
  const autoCompleteProcedures = useOrgSettingsStore((s) => s.autoCompleteProcedures);
  const loaded = useOrgSettingsStore((s) => s.loaded);
  const loadError = useOrgSettingsStore((s) => s.error);
  const fetchProcedurePaymentSettings = useOrgSettingsStore((s) => s.fetchProcedurePaymentSettings);
  const setAutoCompleteProcedures = useOrgSettingsStore((s) => s.setAutoCompleteProcedures);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!loaded) fetchProcedurePaymentSettings();
  }, [loaded, fetchProcedurePaymentSettings]);

  // The GET never resolved (network error / 404 on an old backend) — show
  // the setting as unavailable rather than a possibly-wrong on/off state.
  const unavailable = !loaded && !!loadError;

  const onToggle = async () => {
    if (busy || unavailable) return;
    setBusy(true);
    setErr(null);
    try {
      await setAutoCompleteProcedures(!autoCompleteProcedures);
    } catch (e) {
      const status = e?.response?.status;
      setErr(
        status === 404
          ? 'This setting is not available yet on this server — ask your admin to update the app.'
          : e?.response?.data?.detail || e?.message || 'Failed to update',
      );
    } finally {
      setBusy(false);
    }
  };

  const disabled = busy || unavailable;
  const disabledReason = unavailable
    ? 'Unavailable — could not load this setting'
    : busy
      ? 'Saving…'
      : null;

  return (
    <div className="rounded-2xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-5 mt-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">
            🩺 Procedure autocompletion
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
            When ON, a procedure is marked completed automatically as soon as its payment is
            collected — useful until doctors start logging into the app to complete procedures
            themselves. The visit stays open, and toggling this does not change procedures
            already marked complete.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <button
            type="button"
            role="switch"
            aria-checked={autoCompleteProcedures}
            onClick={onToggle}
            disabled={disabled}
            title={disabledReason || undefined}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60 ${
              autoCompleteProcedures ? 'bg-teal-600' : 'bg-slate-300 dark:bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                autoCompleteProcedures ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
          <span
            className={`text-xs font-semibold ${
              autoCompleteProcedures ? 'text-teal-700 dark:text-teal-300' : 'text-slate-500 dark:text-slate-400'
            }`}
          >
            {unavailable
              ? 'Unavailable'
              : busy
                ? 'Saving…'
                : autoCompleteProcedures
                  ? 'Enabled'
                  : 'Disabled'}
          </span>
        </div>
      </div>

      {unavailable && (
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
          Could not load this setting right now — try refreshing the page.
        </div>
      )}
      {err && <div className="text-xs text-rose-600 dark:text-rose-400 mt-2">{err}</div>}
    </div>
  );
}
